import {
  ApartmentOutlined,
  AppstoreOutlined,
  CheckCircleOutlined,
  ExclamationCircleFilled,
  HolderOutlined,
  PartitionOutlined,
  PlusCircleOutlined,
  ReloadOutlined,
  SaveOutlined,
  SearchOutlined,
  StopOutlined,
  } from "@ant-design/icons";
import { DndContext } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Button,
  Card,
  Empty,
  Flex,
  Input,
  Modal,
  Space,
  Table,
  Tooltip,
  Typography,
} from "antd";
import axios from "axios";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { baseUrl } from "../../components/common/baseUrl";
import { useNotify } from "../../components/Notification";
import PageHeader from "../../components/PageLayout/PageHeader";
import FilterBar from "../../components/PageLayout/FilterBar";
import MetricCard from "../../components/PageLayout/MetricCard";
import { loadMenu } from "../../auth/session";
import { brand, neutral, semantic } from "../../theme/tokens";
import { settings } from "../../utils/config";
import RowActions from "../../components/cms/RowActions";
import { ACTION_ICON, confirmDelete } from "../../components/cms/actions";
import StatusTag from "../../components/cms/StatusTag";

const { Text } = Typography;
const RowContext = createContext({});

const ListMenu = () => {
  const notify = useNotify();
  const navigate = useNavigate();
  const hasAccessToken = settings?.getCookie("access_token");

  const [treeData, setTreeData] = useState([]);
  const [visibleList, setVisibleList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalTreeData, setOriginalTreeData] = useState([]);
  const [searchText, setSearchText] = useState("");

  const isFiltering = searchText.trim().length > 0;

  const buildVisibleList = (tree, level = 0) => {
    if (!Array.isArray(tree)) return [];

    let result = [];
    for (let item of tree) {
      const { children, ...rest } = item;
      result.push({ ...rest, level });
      if (Array.isArray(children) && children.length > 0) {
        result = result.concat(buildVisibleList(children, level + 1));
      }
    }
    return result;
  };

  const visibleRows = useMemo(
    () => (Array.isArray(visibleList) ? visibleList : []),
    [visibleList]
  );
  const tableRows = useMemo(() => {
    const keyword = searchText.trim().toLowerCase();
    if (!keyword) return visibleRows;

    return visibleRows.filter((item) => {
      const name = item.name?.toLowerCase() || "";
      const url = item.url?.toLowerCase() || "";
      const icon = item.icon?.toLowerCase() || "";
      return name.includes(keyword) || url.includes(keyword) || icon.includes(keyword);
    });
  }, [searchText, visibleRows]);

  const stats = useMemo(() => {
    const active = visibleRows.filter((item) => item.is_active === 1).length;
    const parent = visibleRows.filter((item) => item.level === 0).length;
    return {
      total: visibleRows.length,
      parent,
      child: Math.max(visibleRows.length - parent, 0),
      active,
      inactive: Math.max(visibleRows.length - active, 0),
    };
  }, [visibleRows]);

  const fetchActions = async () => {
    try {
      setIsLoading(true);
      const url = `${baseUrl}/api/v1/security/menus`;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${hasAccessToken}` },
      });

      if (res.data.success) {
        const data = Array.isArray(res.data.data) ? res.data.data : [];
        setTreeData(data);
        setOriginalTreeData(JSON.parse(JSON.stringify(data)));
        setVisibleList(buildVisibleList(data));
        setHasChanges(false);
      }
    } catch (err) {
      notify.error({
        message: "Thất bại",
        description: err.response?.data?.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const moveItemInTree = (tree, currentVisibleList, activeId, overId) => {
    const clone = JSON.parse(JSON.stringify(Array.isArray(tree) ? tree : []));

    const findAndRemove = (items, id) => {
      for (let i = 0; i < items.length; i++) {
        if (items[i].id === id) return items.splice(i, 1)[0];
        if (Array.isArray(items[i].children)) {
          const result = findAndRemove(items[i].children, id);
          if (result) return result;
        }
      }
      return null;
    };

    const getParent = (items, id, parent = null) => {
      for (let item of items) {
        if (item.id === id) return parent;
        if (Array.isArray(item.children)) {
          const found = getParent(item.children, id, item);
          if (found) return found;
        }
      }
      return null;
    };

    const insertAtIndex = (items, item, index) => {
      if (index < 0) {
        items.push(item);
      } else {
        items.splice(index, 0, item);
      }
    };

    const activeItem = findAndRemove(clone, activeId);
    if (!activeItem) return tree;

    const activeIndex = currentVisibleList.findIndex((i) => i.id === activeId);
    const overIndex = currentVisibleList.findIndex((i) => i.id === overId);
    const overParent = getParent(clone, overId);
    const siblings = overParent ? overParent.children : clone;
    const siblingOverIndex = siblings.findIndex((i) => i.id === overId);

    if (activeIndex < overIndex) {
      insertAtIndex(siblings, activeItem, siblingOverIndex + 1);
    } else {
      insertAtIndex(siblings, activeItem, siblingOverIndex);
    }

    return clone;
  };

  const handleDragEnd = ({ active, over }) => {
    if (isFiltering || !over || active.id === over.id) return;

    const newTree = moveItemInTree(treeData, visibleRows, active.id, over.id);
    setTreeData(newTree);
    setVisibleList(buildVisibleList(newTree));
    setHasChanges(true);
  };

  const handleSaveLocation = async () => {
    const buildSaveList = (tree, parentId = null) => {
      if (!Array.isArray(tree)) return [];

      return tree.flatMap((item, index) => {
        const newItem = {
          ...item,
          sort_order: index + 1,
          parent_id: parentId,
        };
        return [
          newItem,
          ...(Array.isArray(item.children) ? buildSaveList(item.children, item.id) : []),
        ];
      });
    };

    const currentList = buildSaveList(treeData);
    const originalList = buildSaveList(originalTreeData);
    const changedItems = currentList.filter((item) => {
      const originalItem = originalList.find((o) => o.id === item.id);
      return (
        !originalItem ||
        originalItem.sort_order !== item.sort_order ||
        originalItem.parent_id !== item.parent_id
      );
    });

    try {
      setIsLoading(true);
      const url = `${baseUrl}/api/v1/security/menus/sort`;
      const res = await axios.post(
        url,
        { menu_items: changedItems },
        { headers: { Authorization: `Bearer ${hasAccessToken}` } }
      );

      if (res.data.success) {
        notify.success({
          message: "Thành công",
          description: "Sắp xếp menu thành công",
        });
        // force: vừa đổi thứ tự menu nên phải bỏ qua cache 5 phút
        loadMenu({ force: true });
        setOriginalTreeData(JSON.parse(JSON.stringify(treeData)));
        setHasChanges(false);
      }
    } catch (err) {
      notify.error({
        message: "Thất bại",
        description: err.response?.data?.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const showDeleteConfirm = (record) => {
    Modal.confirm({
      title: `Xóa menu "${record.name}"?`,
      content: "Menu bị xóa sẽ không còn hiển thị trong điều hướng của người dùng.",
      icon: <ExclamationCircleFilled />,
      okText: "Xóa menu",
      okType: "danger",
      cancelText: "Hủy",
      onOk: async () => {
        try {
          await axios.delete(`${baseUrl}/api/v1/security/menus/${record.id}`, {
            headers: { Authorization: `Bearer ${hasAccessToken}` },
          });
          notify.success({
            message: "Thành công",
            description: "Xóa menu thành công",
          });
          fetchActions();
        } catch (err) {
          notify.error({
            message: "Thất bại",
            description: err.response?.data?.message,
          });
        }
      },
    });
  };

  const Row = (props) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      setActivatorNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: props["data-row-key"], disabled: isFiltering });
    const style = {
      ...props.style,
      transform: CSS.Transform.toString(transform),
      transition,
      ...(isDragging ? { position: "relative", zIndex: 1000 } : {}),
    };
    const contextValue = useMemo(
      () => ({ setActivatorNodeRef, listeners }),
      [setActivatorNodeRef, listeners]
    );
    return (
      <RowContext.Provider value={contextValue}>
        <tr {...props} ref={setNodeRef} style={style} {...attributes} />
      </RowContext.Provider>
    );
  };

  const DragHandle = () => {
    const { setActivatorNodeRef, listeners } = useContext(RowContext);
    return (
      <Tooltip title={isFiltering ? "Tắt tìm kiếm để sắp xếp" : "Kéo để đổi thứ tự"}>
        <Button
          type="text"
          size="small"
          disabled={isFiltering}
          icon={<HolderOutlined />}
          style={{ cursor: isFiltering ? "not-allowed" : "grab" }}
          ref={setActivatorNodeRef}
          {...listeners}
        />
      </Tooltip>
    );
  };

  const handleUpdateStatus = async (record) => {
    try {
      setIsLoading(true);

      const response = await axios.put(
        `${baseUrl}/api/v1/security/menus/${record.id}/active`,
        { is_active: !record.is_active },
        { headers: { Authorization: `Bearer ${hasAccessToken}` } }
      );

      if (response) {
        notify.success({
          message: "Thành công",
          description: "Cập nhật trạng thái menu thành công",
        });
        fetchActions();
      }
    } catch (error) {
      notify.error({
        message: "Thất bại",
        description: error.response?.data?.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const columns = [
    {
      title: "Sắp xếp",
      key: "index",
      render: () => <DragHandle />,
      width: 82,
      fixed: "left",
      align: "center",
    },
    {
      title: "Menu",
      key: "name",
      width: 280,
      fixed: "left",
      render: (_, record) => (
        <Flex
          align="center"
          gap={10}
          style={{ paddingLeft: (record.level || 0) * 28 }}
        >
          {record.level === 0 ? (
            <span
              style={{
                width: 28,
                height: 28,
                borderRadius: 6,
                background: "#eff6ff",
                color: brand.accent,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {record.icon ? <i className={record.icon} /> : "M"}
            </span>
          ) : (
            <span style={{ width: 28, textAlign: "center", color: neutral[400] }}>└</span>
          )}
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                color: record.level === 0 ? "#0f172a" : "#334155",
                fontWeight: record.level === 0 ? 700 : 500,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={record.name}
            >
              {record.name || "Chưa đặt tên"}
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.level === 0 ? "Menu cha" : "Menu con"}
            </Text>
          </div>
        </Flex>
      ),
    },
    {
      title: "Đường dẫn",
      dataIndex: "url",
      key: "url",
      width: 260,
      render: (url) =>
        url ? <StatusTag>{url}</StatusTag> : <Text type="secondary">Không có</Text>,
    },
    {
      title: "Trạng thái",
      key: "is_active",
      align: "center",
      width: 150,
      render: (_, record) => (
        <Tooltip title="Bấm để đổi trạng thái hiển thị">
          <StatusTag
            dot
            tone={record.is_active === 1 ? "success" : "default"}
            onClick={() => handleUpdateStatus(record)}
            style={{ cursor: "pointer", minWidth: 98, textAlign: "center" }}
          >
            {record.is_active === 1 ? "Đang bật" : "Đang tắt"}
          </StatusTag>
        </Tooltip>
      ),
    },
    {
      title: "Thao tác",
      key: "actions",
      align: "right",
      width: 150,
      fixed: "right",
      render: (_, record) => (
        <RowActions
          items={[
            {
              key: "edit",
              icon: ACTION_ICON.edit,
              label: "Sửa menu",
              onClick: () => navigate(`/update-menu/${record.id}`),
            },
            {
              key: "delete",
              icon: ACTION_ICON.delete,
              label: "Xóa menu",
              danger: true,
              divider: true,
              // Màn này đã có hộp thoại xác nhận riêng (kèm cảnh báo menu con) —
              // giữ nguyên thay vì thay bằng `confirmDelete` chung.
              onClick: () => showDeleteConfirm(record),
            },
          ]}
        />
      ),
    },
  ];

  useEffect(() => {
    fetchActions();
  }, []);

  return (
    <div>
      <PageHeader
        title="Quản lý Menu"
        subtitle="Quản lý cấu trúc điều hướng, trạng thái hiển thị và thứ tự menu trong CMS."
        breadcrumb={[
          { title: "Phân quyền" },
          { title: <NavLink to="/menu">Quản lý Menu</NavLink> },
        ]}
        extra={
          <Space wrap>
            {hasChanges && <StatusTag tone="warning">Có thay đổi chưa lưu</StatusTag>}
            <Button icon={<ReloadOutlined />} onClick={fetchActions} loading={isLoading}>
              Tải lại
            </Button>
            <Tooltip title={isFiltering ? "Tắt tìm kiếm để lưu thứ tự" : "Lưu thứ tự menu hiện tại"}>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSaveLocation}
                disabled={!hasChanges || isFiltering}
                loading={isLoading && hasChanges}
              >
                Lưu thứ tự
              </Button>
            </Tooltip>
            <NavLink to="/create-menu">
              <Button type="primary" icon={<PlusCircleOutlined />}>
                Thêm menu
              </Button>
            </NavLink>
          </Space>
        }
      />

      <div style={{ display: "flex", flexWrap: "wrap", gap: 16, padding: "16px 16px 0" }}>
        <div style={{ flex: "1 1 160px", minWidth: 160 }}>
          <MetricCard icon={<AppstoreOutlined />} color={brand.accent} label="Tổng menu" value={stats.total} />
        </div>
        <div style={{ flex: "1 1 160px", minWidth: 160 }}>
          <MetricCard icon={<ApartmentOutlined />} color={semantic.info} label="Menu cha" value={stats.parent} />
        </div>
        <div style={{ flex: "1 1 160px", minWidth: 160 }}>
          <MetricCard icon={<PartitionOutlined />} color={neutral[500]} label="Menu con" value={stats.child} />
        </div>
        <div style={{ flex: "1 1 160px", minWidth: 160 }}>
          <MetricCard icon={<CheckCircleOutlined />} color={semantic.success} label="Đang bật" value={stats.active} />
        </div>
        <div style={{ flex: "1 1 160px", minWidth: 160 }}>
          <MetricCard icon={<StopOutlined />} color={semantic.danger} label="Đang tắt" value={stats.inactive} />
        </div>
      </div>

      <FilterBar style={{ marginTop: 16, borderTop: "1px solid #F1F5F9" }}>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Tìm theo tên, đường dẫn hoặc icon..."
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          style={{ width: 360, maxWidth: "100%" }}
        />
        {isFiltering && (
          <Text type="secondary">
            Đang lọc {tableRows.length}/{visibleRows.length} menu · tắt tìm kiếm để kéo thả & lưu thứ tự.
          </Text>
        )}
      </FilterBar>

      <Card
        style={{ margin: 16 }}
        title={
          <Space>
            <AppstoreOutlined />
            <span>Danh sách menu</span>
          </Space>
        }
      >
        <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={handleDragEnd}>
          <SortableContext
            items={tableRows.map((item) => item.id)}
            strategy={verticalListSortingStrategy}
          >
            <Table
              className="custom-table"
              columns={columns}
              dataSource={tableRows}
              rowKey="id"
              loading={isLoading}
              components={{ body: { row: Row } }}
              pagination={false}
              size="middle"
              scroll={{ y: 55 * 10, x: "max-content" }}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={isFiltering ? "Không tìm thấy menu phù hợp" : "Chưa có menu"}
                  />
                ),
              }}
            />
          </SortableContext>
        </DndContext>
      </Card>
    </div>
  );
};

export default ListMenu;